import {
  CANCEL_APPROVAL_TYPES,
  CANCEL_EXTRA_ORIGIN_TYPES,
  CANCEL_ORIGIN_TYPE,
} from '@/constant';
import { SUBJECT_MAX_LENGTH } from '@/constant/approval';
import type { CancelApprovalAttributes } from '@/types/approval';
import { localize } from '@/utils/approval/localize';

/**
 * 취소 대상 원 품의 스냅샷 — 작성 폼(RHF cancelTarget)·조회 슬롯 공용.
 * 조회 attributes 와 같은 4키. 저장 요청엔 approvalId 만 실림(buildCancelAttributes)
 */
export type CancelTargetSnapshot = {
  approvalId: string;
  subject: string;
  /** YYYY-MM-DD */
  submittedAt: string;
  url: string;
};

/** 226/227/232/237/238/239 — 작성 폼·저장 attributes·조회 슬롯을 한 구현이 공유하는 판정 */
export const isCancelApprovalType = (apvType: number): boolean =>
  CANCEL_APPROVAL_TYPES.includes(apvType);

/** 취소 양식 → 취소 대상 원 품의 유형. 취소 양식이 아니면 undefined */
export const getCancelOriginType = (apvType: number): number | undefined =>
  CANCEL_ORIGIN_TYPE[apvType];

/** 취소 양식이 이 원 품의 유형을 취소 대상으로 받는지 */
export const isCancelOriginOf = (
  formType: number,
  originType: number,
): boolean =>
  CANCEL_ORIGIN_TYPE[formType] === originType ||
  (CANCEL_EXTRA_ORIGIN_TYPES[formType] ?? []).includes(originType);

/** 원 품의 유형 → 취소 양식(역매핑). 조회 「품의 취소」·마이페이지 진입이 씀. 대응 양식 없으면 undefined */
export const getCancelFormType = (originType: number): number | undefined =>
  CANCEL_APPROVAL_TYPES.find((formType) =>
    isCancelOriginOf(formType, originType),
  );

/** 작성 화면 진입 쿼리 키 — 레거시 `/intranet/approval/create?apv_type=&doc=` 와 동일 */
export const CANCEL_ENTRY_QUERY_KEY = 'doc';

/** 진입 쿼리 → 원 품의 문서번호. 숫자 문자열만 인정, 반복 키는 첫 값 */
export const getEntryCancelTargetDocId = (
  query: Record<string, string | string[] | undefined>,
): string | undefined => {
  const raw = query[CANCEL_ENTRY_QUERY_KEY];
  const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
  return value && /^\d+$/.test(value) ? value : undefined;
};

/**
 * 단건조회 attributes → 스냅샷. approvalId 가 없으면 취소 대상 미확정(null).
 * attributes 는 Record<string, unknown> 이라 문자열 아닌 값은 빈 문자열로 받음
 */
export const parseCancelTargetSnapshot = (
  attributes: unknown,
): CancelTargetSnapshot | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const a = attributes as Record<string, unknown>;
  const text = (v: unknown): string =>
    typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
  const approvalId = text(a.approvalId);
  if (!approvalId) return null;
  return {
    approvalId,
    subject: text(a.subject),
    submittedAt: text(a.submittedAt),
    url: text(a.url),
  };
};

/** 저장(POST/PUT) attributes — 스냅샷 없으면 null(서버가 approvalId 필수로 안내) */
export const buildCancelAttributes = (
  snapshot: CancelTargetSnapshot | null | undefined,
): CancelApprovalAttributes | null =>
  snapshot ? { approvalId: snapshot.approvalId } : null;

/** 취소 품의 제목 자동값 — 서버 subject 200자 제한에 맞춰 잘라냄 */
export const buildCancelSubject = (originSubject: string): string =>
  localize('품의 취소 신청 ({{subject}})', { subject: originSubject }).slice(
    0,
    SUBJECT_MAX_LENGTH,
  );
